import React, { useEffect, useRef, useState } from "react";

export default function Contacto() {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    asunto: "",
    mensaje: "",
  });

  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Formulario enviado 🚀");

  // El ref bloquea un segundo clic de forma inmediata, incluso antes del render.
  const submittingRef = useRef(false);
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const handleChange = (e) => {
    if (submittingRef.current) return;
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    setSent(false);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || sent) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const res = await fetch(
        "https://us-central1-ekineapp.cloudfunctions.net/sendContactMessage",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "No se pudo enviar");

      setSuccessMessage(
        json.duplicado
          ? "Este mensaje ya fue recibido recientemente. No enviamos otra copia."
          : "Formulario enviado 🚀"
      );
      setSent(true);
      setFormData({ nombre: "", email: "", telefono: "", asunto: "", mensaje: "" });
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error("Error al enviar el contacto:", err);
      alert("No fue posible enviar tu mensaje. Por favor, inténtalo nuevamente.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen pt-28 md:pt-36 pb-24 px-6 md:px-20 overflow-hidden bg-[#E5E5E5]">

      {/* Encabezado */}
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#00698F]">
          Envíanos tus consultas
        </h1>
        <p className="text-[#00698F] mt-3">
          Completa el siguiente formulario y te responderemos a la brevedad.
        </p>
      </div>

      {/* Formulario en tarjeta estilo glass */}
      <form
        onSubmit={handleSubmit}
        aria-busy={isSubmitting}
        className="
          max-w-4xl mx-auto bg-white/60 backdrop-blur-xl rounded-3xl
          shadow-[0_30px_80px_-30px_rgba(58,106,255,0.35)]
          ring-1 ring-blue-100 p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6
        "
      >
        {/* Nombre */}
        <div className="flex flex-col">
          <label htmlFor="nombre" className="font-semibold text-[#0A1440] mb-1">
            Nombre Completo
          </label>
          <input
            type="text"
            name="nombre"
            id="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="border border-blue-100 rounded-xl px-3 py-2 bg-white/90 focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]"
          />
        </div>

        {/* Email */}
        <div className="flex flex-col">
          <label htmlFor="email" className="font-semibold text-[#0A1440] mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="border border-blue-100 rounded-xl px-3 py-2 bg-white/90 focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]"
          />
        </div>

        {/* Teléfono */}
        <div className="flex flex-col">
          <label htmlFor="telefono" className="font-semibold text-[#0A1440] mb-1">
            Teléfono
          </label>
          <input
            type="tel"
            name="telefono"
            id="telefono"
            value={formData.telefono}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="border border-blue-100 rounded-xl px-3 py-2 bg-white/90 focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]"
          />
        </div>

        {/* Asunto */}
        <div className="flex flex-col">
          <label htmlFor="asunto" className="font-semibold text-[#0A1440] mb-1">
            Asunto
          </label>
          <input
            type="text"
            name="asunto"
            id="asunto"
            value={formData.asunto}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="border border-blue-100 rounded-xl px-3 py-2 bg-white/90 focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]"
          />
        </div>

        {/* Mensaje */}
        <div className="md:col-span-2 flex flex-col">
          <label htmlFor="mensaje" className="font-semibold text-[#0A1440] mb-1">
            Mensaje
          </label>
          <textarea
            name="mensaje"
            id="mensaje"
            rows="5"
            value={formData.mensaje}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="border border-blue-100 rounded-xl px-3 py-2 bg-white/90 resize-none focus:outline-none focus:ring-2 focus:ring-[#3A6AFF]"
          ></textarea>
        </div>

        {/* Botón bloqueado durante el envío y después de una respuesta exitosa */}
        <div className="md:col-span-2 text-center mt-4">
          <button
            type="submit"
            disabled={isSubmitting || sent}
            aria-disabled={isSubmitting || sent}
            className="px-7 py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 bg-[#06b6fa] hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
          >
            {isSubmitting && (
              <span
                aria-hidden="true"
                className="h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin"
              />
            )}
            {isSubmitting ? "Enviando mensaje..." : sent ? "¡Mensaje recibido!" : "Enviar"}
          </button>
        </div>
      </form>

      {/* Mensaje flotante de éxito */}
      {sent && (
        <div role="status" aria-live="polite" className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-green-600 text-white px-5 py-2 rounded-lg shadow-lg animate-fadeIn text-center">
          {successMessage}
        </div>
      )}
    </section>
  );
}
